import type {
  ConnectedAccount,
  DispatchRecord,
  PlatformInfo,
  ScheduledPost,
} from "../api";
import { PlatformIcon } from "./PlatformIcon";
import { Thumbnail } from "./Thumbnail";
import { humanizeDispatchError, isPendingPlatformError } from "../lib/errorMessage";

function timeOfDay(at: number): string {
  return new Date(at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/** Short "Thu 9:00 AM" label for the next upcoming post. */
function nextLabel(at: number): string {
  return new Date(at).toLocaleString(undefined, {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * The right-hand rail beside the calendar: today's scheduled posts, and a
 * "Needs attention" block surfacing real issues — accounts that must be
 * reconnected and posts that failed to publish. Everything is derived from live
 * data by the parent; this component only renders and routes clicks.
 */
export function TodayRail({
  today,
  todayPosts,
  platforms,
  reconnects,
  failures,
  onSelectPost,
  onReconnect,
  onViewFailure,
  onDismissFailure,
  onViewFullDay,
  variant = "side",
  upcoming,
}: {
  today: Date;
  todayPosts: ScheduledPost[];
  /** Forward-looking summary for the top band: how many posts are scheduled in
   * the next 7 days and when the very next one goes out. Omitted in side mode. */
  upcoming?: { soon: number; nextAt: number | null };
  platforms: PlatformInfo[];
  reconnects: ConnectedAccount[];
  failures: DispatchRecord[];
  onSelectPost: (post: ScheduledPost) => void;
  onReconnect: () => void;
  /** Open the real details for a specific failed dispatch (the parent routes to
   * the calendar day-detail, which shows each leg's error). */
  onViewFailure: (rec: DispatchRecord) => void;
  /** Dismiss a failed item so it stops nagging (persisted by the parent). */
  onDismissFailure: (rec: DispatchRecord) => void;
  onViewFullDay: () => void;
  /** "side" is the sticky right-hand rail; "top" is a full-width band that sits
   * above the calendar (its cards flow side by side instead of stacking). */
  variant?: "side" | "top";
}) {
  const platformById = new Map(platforms.map((p) => [p.id, p]));
  const hasIssues = reconnects.length > 0 || failures.length > 0;
  const dateLabel = today.toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });

  return (
    <div className={`cc-rail${variant === "top" ? " cc-rail--top" : ""}`}>
      <div className="cc-rail-card">
        <div className="cc-rail-title">
          <h3>Today</h3>
          <span className="cc-rail-date">{dateLabel}</span>
        </div>

        {todayPosts.length === 0 ? (
          <p className="cc-rail-empty">Nothing scheduled today — upcoming posts land here.</p>
        ) : (
          <div className="cc-rail-list">
            {todayPosts.map((post) => {
              const first = platformById.get(post.platforms[0]!);
              return (
                <button
                  key={post.id}
                  type="button"
                  className="cc-rail-item"
                  onClick={() => onSelectPost(post)}
                >
                  <Thumbnail
                    mediaUrl={post.draft.media?.url}
                    mediaKind={post.draft.media?.kind}
                    platform={first}
                  />
                  <span className="cc-rail-item-body">
                    <span className="cc-rail-item-title">
                      {post.draft.title || "Untitled post"}
                    </span>
                    <span className="cc-rail-item-meta">
                      {post.platforms
                        .map((id) => platformById.get(id))
                        .filter((p): p is PlatformInfo => Boolean(p))
                        .map((p) => (
                          <PlatformIcon key={p.id} platform={p} size={14} />
                        ))}
                      <span>· {timeOfDay(post.at)}</span>
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {todayPosts.length > 0 && (
          <button type="button" className="link-btn cc-rail-link" onClick={onViewFullDay}>
            View full day →
          </button>
        )}
      </div>

      {/* Forward-looking summary tile — turns the band into an at-a-glance
          overview instead of only "today". Top band only. */}
      {upcoming && (
        <div className="cc-rail-card">
          <div className="cc-rail-title">
            <h3>Upcoming</h3>
          </div>
          <p className="cc-rail-figure">
            <span className="cc-rail-figure-n">{upcoming.soon}</span>
            {upcoming.soon === 1 ? "post in the next 7 days" : "posts in the next 7 days"}
          </p>
          <p className="cc-rail-empty">
            {upcoming.nextAt ? `Next: ${nextLabel(upcoming.nextAt)}` : "No posts scheduled yet."}
          </p>
          <button type="button" className="link-btn cc-rail-link" onClick={onViewFullDay}>
            Open calendar →
          </button>
        </div>
      )}

      {/* Absence of problems removes UI rather than creating a reassuring card:
          the "Needs attention" block only appears when something genuinely does. */}
      {hasIssues && (
        <div className="cc-rail-card">
          <div className="cc-rail-title">
            <h3 className="cc-attn-title">Needs attention</h3>
          </div>

          <div className="cc-attn-list">
            {reconnects.map((acc) => {
              const p = platformById.get(acc.platform);
              return (
                <div key={`rc-${acc.platform}`} className="cc-attn-item">
                  <span className="cc-attn-dot" aria-hidden="true" />
                  <div className="cc-attn-body">
                    <div className="cc-attn-head">{p?.name ?? acc.platform} needs reconnecting</div>
                    <div className="cc-attn-sub">Its access expired — posts won't publish until you reconnect.</div>
                    <button type="button" className="cc-attn-action" onClick={onReconnect}>
                      Reconnect →
                    </button>
                  </div>
                </div>
              );
            })}

            {failures.map((rec) => {
              const failed = rec.results.filter(
                (r) => !r.ok && !isPendingPlatformError(r.error),
              );
              const first = failed[0];
              return (
                <div key={`f-${rec.id}`} className="cc-attn-item is-err">
                  <span className="cc-attn-dot" aria-hidden="true" />
                  <div className="cc-attn-body">
                    <div className="cc-attn-head">{rec.title || "Untitled post"} failed to publish</div>
                    <div className="cc-attn-sub">
                      {first ? humanizeDispatchError(first.error) : "One or more platforms failed."}
                    </div>
                    <button
                      type="button"
                      className="cc-attn-action"
                      onClick={() => onViewFailure(rec)}
                    >
                      View details →
                    </button>
                  </div>
                  <button
                    type="button"
                    className="cc-attn-dismiss"
                    aria-label="Dismiss"
                    title="Dismiss"
                    onClick={() => onDismissFailure(rec)}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
